import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminGetCashControlAccount,
  adminCreateCashControlAccount,
  adminExportGlobalAccountStatement,
  adminGetGlobalAccountCreationOptions,
  adminInitializeBankingAccount,
} from '../services/admin';
import {
  adminBlockAccountDebits,
  adminGetAccountRfis,
  adminGetAccountRfiDetail,
  adminUnblockAccountDebits,
  getAllAccountsRfis,
} from '../services/banking';
import {
  ConnectedAccount,
  DebitsBlockStatus,
  GlobalAccountCreationOption,
  PendingRfi,
  RFIStatus,
  RfisCountResponse,
} from '../types/banking';
import { Nullable } from '../types/utils';
import { BANKING_DETAILS_QUERY_KEY } from './useBanking';

interface UseAdminBankingParams {
  companyId?: number;
}

export const CASH_CONTROL_ACCOUNT_QUERY_KEY = 'adminCashControlAccount';

export const useCashControlAccount = ({ companyId }: UseAdminBankingParams) => {
  return useQuery<Nullable<ConnectedAccount>>({
    queryKey: [CASH_CONTROL_ACCOUNT_QUERY_KEY, companyId],
    queryFn: () => adminGetCashControlAccount(companyId!),
    enabled: !!companyId,
  });
};

export const useCreateCashControlAccount = ({ companyId }: UseAdminBankingParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => adminCreateCashControlAccount(companyId!),
    onSuccess: () => {
      // Invalidate and refetch the cash control account query
      queryClient.invalidateQueries({ queryKey: [CASH_CONTROL_ACCOUNT_QUERY_KEY, companyId] });
    },
  });
};

export const useInitializeBankingAccount = ({ companyId }: UseAdminBankingParams) => {
  return useMutation({
    mutationFn: () => adminInitializeBankingAccount(companyId!),
  });
};

interface UseExportGlobalAccountStatementParams {
  accountId?: number;
}

export const useExportGlobalAccountStatement = ({ accountId }: UseExportGlobalAccountStatementParams) => {
  return useMutation<Blob, Error, { globalAccountId: string }>({
    mutationFn: ({ globalAccountId }) => adminExportGlobalAccountStatement(accountId!, globalAccountId),
  });
};

interface UseAdminAccountRfisParams {
  accountId?: number;
  page?: number;
  pageSize?: number;
  status?: string[];
  rfiType?: string[];
}

export const ADMIN_ACCOUNT_RFIS_QUERY_KEY = 'adminAccountRfis';

export const useAdminAccountRfis = ({
  accountId,
  page = 1,
  pageSize = 10,
  status,
  rfiType,
}: UseAdminAccountRfisParams) => {
  return useQuery<RfisCountResponse>({
    queryKey: [ADMIN_ACCOUNT_RFIS_QUERY_KEY, accountId, page, pageSize, status, rfiType],
    queryFn: () => adminGetAccountRfis(accountId!, { page, pageSize, status, rfiType }),
    enabled: !!accountId,
  });
};

interface UseAdminAllAccountsRfisParams {
  page?: number;
  pageSize?: number;
  status?: string[];
  rfiType?: string[];
  search?: string;
}

export const ADMIN_ALL_ACCOUNTS_RFIS_QUERY_KEY = 'adminAllAccountsRfis';

export const useAdminAllAccountsRfis = ({
  page = 1,
  pageSize = 10,
  status,
  rfiType,
  search,
}: UseAdminAllAccountsRfisParams) => {
  return useQuery<RfisCountResponse>({
    queryKey: [ADMIN_ALL_ACCOUNTS_RFIS_QUERY_KEY, page, pageSize, status, rfiType, search],
    queryFn: () => getAllAccountsRfis(pageSize, { page, status, rfiType, search }),
  });
};

interface UseAdminAccountRfiDetailParams {
  accountId?: number;
  id?: number;
}

export const ADMIN_ACCOUNT_RFI_DETAIL_QUERY_KEY = 'adminAccountRfiDetail';

// Fetches a single RFI for the admin detail drawer (refreshing only that one from
// Airwallex), mirroring the client-side useRfiDetail.
export const useAdminAccountRfiDetail = ({ accountId, id }: UseAdminAccountRfiDetailParams) => {
  return useQuery<PendingRfi>({
    queryKey: [ADMIN_ACCOUNT_RFI_DETAIL_QUERY_KEY, accountId, id],
    queryFn: () => adminGetAccountRfiDetail(accountId!, id!),
    enabled: !!accountId && !!id,
  });
};

// Set of Airwallex transaction ids that currently have a pending RFI, for the
// admin transaction tables (mirrors the client-side useRfiSourceIds).
export const useAdminRfiSourceIds = ({ accountId }: { accountId?: number }): Set<string> => {
  const { data } = useAdminAccountRfis({
    accountId,
    page: 1,
    pageSize: 100,
    status: [RFIStatus.ACTION_REQUIRED],
  });

  return useMemo(
    () => new Set((data?.results ?? []).flatMap((rfi) => (rfi.sources ?? []).map((s) => s.sourceId))),
    [data]
  );
};

export const ADMIN_GLOBAL_ACCOUNT_CREATION_OPTIONS_QUERY_KEY = 'adminGlobalAccountCreationOptions';

export const useAdminGlobalAccountCreationOptions = ({ accountId }: { accountId?: number }) => {
  return useQuery<GlobalAccountCreationOption[]>({
    queryKey: [ADMIN_GLOBAL_ACCOUNT_CREATION_OPTIONS_QUERY_KEY, accountId],
    queryFn: () => adminGetGlobalAccountCreationOptions(accountId!),
    enabled: !!accountId,
  });
};

export const useSetAccountDebitsBlocked = () => {
  const queryClient = useQueryClient();

  return useMutation<DebitsBlockStatus, Error, { accountId: number; blocked: boolean }>({
    mutationFn: ({ accountId, blocked }) =>
      blocked ? adminBlockAccountDebits(accountId) : adminUnblockAccountDebits(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CASH_CONTROL_ACCOUNT_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BANKING_DETAILS_QUERY_KEY] });
    },
  });
};
